// ========================================
// components/admin/PlanConfig.js - Plan Configuration Editor
// ========================================
// FILE PATH: frontend/src/components/admin/PlanConfig.js
// PURPOSE: Admin interface for editing plan limits and ONE-TIME pricing
// 
// LAST MODIFIED: April 9, 2026 - Converted to ONE-TIME PAYMENT model
// MODIFIED BY: System Administrator
// 
// IMPORTANT FOR FUTURE MAINTAINERS:
// - Pricing is now ONE-TIME (lifetime access) - NO recurring billing
// - The pricing field is a simple NUMBER (not yearly/monthly object)
// - Verification choices determine how many trustees must confirm death
// ========================================
// 
// FEATURES:
//   - Edit plan limits (globalNoks, totalAssets, trustees, etc.)
//   - Edit verification threshold options
//   - Edit ONE-TIME pricing (lifetime access)
//   - Save changes to database
//   - View change history
// ========================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PlanConfig = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});

  // ========================================
  // FETCH PLAN CONFIGURATIONS FROM BACKEND
  // ========================================
  // Runs once when component mounts
  // Fetches all plans with their limits and ONE-TIME pricing
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/plans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPlans(response.data);
    } catch (err) {
      setError('Failed to load plan configurations');
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // START EDITING A PLAN
  // ========================================
  // Populates form with selected plan's current values
  const startEdit = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      limits: { ...plan.limits },
      pricing: plan.pricing  // NOTE: Now a simple NUMBER, not an object
    });
    setEditMode(true);
    setError('');
    setSuccess('');
  };

  // ========================================
  // HANDLE LIMIT FIELD CHANGES
  // ========================================
  // Updates form state when admin changes any limit field
  // Converts input to integer (defaults to 0 if invalid)
  const handleLimitChange = (field, value) => {
    setFormData({
      ...formData,
      limits: {
        ...formData.limits,
        [field]: parseInt(value) || 0
      }
    });
  };

  // ========================================
  // HANDLE VERIFICATION CHOICES CHANGE
  // ========================================
  // Verification choices determine how many trustees must confirm death
  // Example: [1,2,3] means user can choose: 1, 2, or 3 trustees to verify
  // Input format: comma-separated numbers (e.g., "1, 2, 3")
  const handleVerificationChoicesChange = (value) => {
    // Convert comma-separated string to array of numbers
    const choicesArray = value.split(',')
      .map(v => parseInt(v.trim()))
      .filter(v => !isNaN(v));
    
    setFormData({
      ...formData,
      limits: {
        ...formData.limits,
        verificationChoices: choicesArray
      }
    });
  };

  // ========================================
  // HANDLE PRICING CHANGE - ONE-TIME PAYMENT
  // ========================================
  // UPDATED: April 9, 2026 - Removed yearly/monthly, now simple number
  // pricingValue: The ONE-TIME amount in GHS (Ghana Cedis)
  const handlePricingChange = (pricingValue) => {
    setFormData({
      ...formData,
      pricing: parseInt(pricingValue) || 0  // Simple number, not an object
    });
  };

  // ========================================
  // SAVE PLAN CONFIGURATION TO DATABASE
  // ========================================
  // Sends PUT request to backend with updated limits and pricing
  // Shows success/error message based on response
  const savePlan = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/plans/${selectedPlan.plan}`,
        formData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSuccess(`${selectedPlan.plan.toUpperCase()} plan updated successfully!`);
      setEditMode(false);
      fetchPlans(); // Refresh data to show latest changes
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  // ========================================
  // CANCEL EDITING (NO CHANGES SAVED)
  // ========================================
  const cancelEdit = () => {
    setEditMode(false);
    setSelectedPlan(null);
    setFormData({});
    setError('');
  };

  // ========================================
  // FORMAT VERIFICATION CHOICES FOR DISPLAY
  // ========================================
  // Takes array like [1,2,3] and returns string like "1 of X, 2 of X, 3 of X"
  // Where X is the maximum number of trustees for this plan
  const getVerificationChoicesText = (choices, maxTrustees) => {
    if (!choices || choices.length === 0) return 'Not configured';
    return choices.map(c => `${c} of ${maxTrustees} trustees`).join(', ');
  };

  // ========================================
  // RENDER EDIT FORM (MODAL-LIKE CARD)
  // ========================================
  // Shows when admin clicks "Edit" on a plan
  // Contains all editable fields with current values
  const renderEditForm = () => {
    if (!selectedPlan) return null;

    const verificationChoicesValue = formData.limits?.verificationChoices?.join(', ') || '';

    return (
      <div style={styles.editCard}>
        <h3 style={styles.editTitle}>
          ✏️ Editing: {selectedPlan.plan.toUpperCase()} Plan
        </h3>
        
        <div style={styles.editGrid}>
          {/* LEFT COLUMN: Feature Limits */}
          <div style={styles.editSection}>
            <h4 style={styles.sectionTitle}>📊 Feature Limits</h4>
            
            <div style={styles.inputGroup}>
              <label>Global Next of Kin (Maximum)</label>
              <input
                type="number"
                min="1"
                value={formData.limits.globalNoks}
                onChange={(e) => handleLimitChange('globalNoks', e.target.value)}
                style={styles.input}
              />
              <small style={styles.hint}>Emergency contacts who inherit if no beneficiaries</small>
            </div>
            
            <div style={styles.inputGroup}>
              <label>Inline Beneficiaries per Asset</label>
              <input
                type="number"
                min="0"
                value={formData.limits.inlinePerAsset}
                onChange={(e) => handleLimitChange('inlinePerAsset', e.target.value)}
                style={styles.input}
              />
              <small style={styles.hint}>Beneficiaries stored directly in asset metadata</small>
            </div>
            
            <div style={styles.inputGroup}>
              <label>External Beneficiaries per Asset</label>
              <input
                type="number"
                min="0"
                value={formData.limits.externalPerAsset}
                onChange={(e) => handleLimitChange('externalPerAsset', e.target.value)}
                style={styles.input}
              />
              <small style={styles.hint}>Beneficiaries with Agyapadie accounts</small>
            </div>
            
            <div style={styles.inputGroup}>
              <label>Total Assets (Account-wide)</label>
              <input
                type="number"
                min="1"
                value={formData.limits.totalAssets}
                onChange={(e) => handleLimitChange('totalAssets', e.target.value)}
                style={styles.input}
              />
              <small style={styles.hint}>Maximum assets user can create across their account</small>
            </div>
            
            <div style={styles.inputGroup}>
              <label>Trustees (Estate Executors)</label>
              <input
                type="number"
                min="1"
                value={formData.limits.trustees}
                onChange={(e) => handleLimitChange('trustees', e.target.value)}
                style={styles.input}
              />
              <small style={styles.hint}>Maximum number of trustees allowed for this plan</small>
            </div>
            
            <div style={styles.inputGroup}>
              <label>File Upload Size Limit (MB)</label>
              <input
                type="number"
                min="1"
                value={formData.limits.fileUploadSizeMB}
                onChange={(e) => handleLimitChange('fileUploadSizeMB', e.target.value)}
                style={styles.input}
              />
              <small style={styles.hint}>Maximum size per uploaded file</small>
            </div>
            
            {/* Verification Threshold Options */}
            <div style={styles.inputGroup}>
              <label>🔐 Verification Threshold Options</label>
              <input
                type="text"
                value={verificationChoicesValue}
                onChange={(e) => handleVerificationChoicesChange(e.target.value)}
                style={styles.input}
                placeholder="e.g., 1, 2, 3"
              />
              <small style={styles.hint}>
                <strong>How it works:</strong> Numbers represent how many trustees must confirm death.
                <br/>
                Example: "1, 2, 3" means users can choose: 1, 2, or 3 trustees to verify.
                <br/>
                <strong>Current:</strong> {getVerificationChoicesText(
                  formData.limits?.verificationChoices, 
                  formData.limits?.trustees
                )}
              </small>
            </div>
          </div>
          
          {/* RIGHT COLUMN: ONE-TIME PRICING */}
          <div style={styles.editSection}>
            <h4 style={styles.sectionTitle}>💰 Pricing (GHS - Ghana Cedis)</h4>
            
            <div style={styles.pricingHighlight}>
              <p style={styles.pricingNote}>
                <strong>✅ ONE-TIME PAYMENT MODEL</strong><br/>
                User pays once, gets LIFETIME access.<br/>
                NO recurring subscriptions or hidden fees.
              </p>
            </div>
            
            <div style={styles.inputGroup}>
              <label>💎 One-Time Price (Lifetime Access)</label>
              <input
                type="number"
                min="0"
                value={formData.pricing}
                onChange={(e) => handlePricingChange(e.target.value)}
                style={styles.pricingInput}
              />
              <small style={styles.hint}>
                Amount in GHS. User pays this once and keeps plan forever.
                <br/>
                Current: <strong>GHS {formData.pricing?.toLocaleString() || 0}</strong>
              </small>
            </div>

            {/* Plan Comparison Note */}
            <div style={styles.infoBox}>
              <strong>📌 Plan Pricing Summary:</strong>
              <ul style={styles.infoList}>
                <li>Free: GHS 0</li>
                <li>Premium: GHS 399 (one-time)</li>
                <li>Business: GHS 999 (one-time)</li>
                <li>Enterprise: GHS 2,999 (one-time)</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div style={styles.editActions}>
          <button onClick={savePlan} disabled={saving} style={styles.saveButton}>
            {saving ? '💾 Saving...' : '✅ Save Changes'}
          </button>
          <button onClick={cancelEdit} style={styles.cancelButton}>
            ❌ Cancel
          </button>
        </div>
      </div>
    );
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading plan configurations...</p>
      </div>
    );
  }

  // ========================================
  // MAIN RENDER - PLAN LIST VIEW
  // ========================================
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>⚙️ Plan Configuration</h2>
        <p style={styles.subtitle}>
          Edit plan limits, verification thresholds, and ONE-TIME pricing. Changes take effect immediately.
        </p>
        <div style={styles.banner}>
          💡 <strong>Pricing Model:</strong> ONE-TIME PAYMENT (Lifetime Access) - No recurring subscriptions
        </div>
      </div>

      {error && <div style={styles.errorBox}>❌ {error}</div>}
      {success && <div style={styles.successBox}>✅ {success}</div>}

      {editMode ? (
        renderEditForm()
      ) : (
        <div style={styles.plansGrid}>
          {plans.map((plan) => (
            <div key={plan.plan} style={styles.planCard}>
              <div style={styles.planHeader}>
                <h3 style={styles.planName}>
                  {plan.plan === 'free' ? '🎁' : plan.plan === 'premium' ? '⭐' : plan.plan === 'business' ? '🏢' : '👑'} 
                  {' '}{plan.plan.toUpperCase()}
                </h3>
                <button onClick={() => startEdit(plan)} style={styles.editButton}>
                  ✏️ Edit
                </button>
              </div>
              
              {/* Feature Limits Section */}
              <div style={styles.planSection}>
                <h4 style={styles.sectionTitle}>📊 Limits</h4>
                <div style={styles.planDetail}>
                  <span>Global Next of Kin:</span>
                  <strong>{plan.limits.globalNoks}</strong>
                </div>
                <div style={styles.planDetail}>
                  <span>Inline per Asset:</span>
                  <strong>{plan.limits.inlinePerAsset}</strong>
                </div>
                <div style={styles.planDetail}>
                  <span>External per Asset:</span>
                  <strong>{plan.limits.externalPerAsset}</strong>
                </div>
                <div style={styles.planDetail}>
                  <span>Total Assets:</span>
                  <strong>{plan.limits.totalAssets}</strong>
                </div>
                <div style={styles.planDetail}>
                  <span>Trustees:</span>
                  <strong>{plan.limits.trustees}</strong>
                </div>
                <div style={styles.planDetail}>
                  <span>File Upload:</span>
                  <strong>{plan.limits.fileUploadSizeMB} MB</strong>
                </div>
                <div style={styles.planDetail}>
                  <span>Verification Choices:</span>
                  <strong>
                    {getVerificationChoicesText(plan.limits.verificationChoices, plan.limits.trustees)}
                  </strong>
                </div>
              </div>
              
              {/* ONE-TIME PRICING Section */}
              <div style={styles.planSection}>
                <h4 style={styles.sectionTitle}>💰 Pricing (One-Time)</h4>
                <div style={styles.priceHighlight}>
                  <span style={styles.priceLabel}>Lifetime Access:</span>
                  <strong style={styles.priceValue}>
                    GHS {plan.pricing?.toLocaleString() || 0}
                  </strong>
                </div>
                <div style={styles.paymentNote}>
                  💳 One-time payment • Never expires • No renewals
                </div>
              </div>
              
              {/* Audit Footer */}
              <div style={styles.planFooter}>
                <span style={styles.lastUpdated}>
                  📅 Last updated: {new Date(plan.updatedAt).toLocaleDateString()}
                </span>
                {plan.updatedBy && (
                  <span style={styles.updatedBy}>👤 by {plan.updatedBy}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========================================
// STYLES - One-Time Payment Theme
// ========================================
// Updated: April 9, 2026 - Added pricing-specific styles
const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#F5F0E6',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '24px',
    color: '#1A1A1A',
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '10px',
  },
  banner: {
    backgroundColor: '#E8F5E9',
    borderLeft: '4px solid #2E7D32',
    padding: '10px 15px',
    borderRadius: '5px',
    fontSize: '13px',
    color: '#1B5E20',
    marginTop: '10px',
  },
  plansGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px',
  },
  planCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  planHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '2px solid #FFD700',
  },
  planName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#2E7D32',
    margin: 0,
  },
  editButton: {
    padding: '4px 12px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  planSection: {
    marginBottom: '15px',
  },
  sectionTitle: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
    borderBottom: '1px solid #eee',
    paddingBottom: '4px',
  },
  planDetail: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '13px',
  },
  priceHighlight: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '8px',
    padding: '8px',
    backgroundColor: '#FFF8E1',
    borderRadius: '5px',
  },
  priceLabel: {
    fontSize: '13px',
    color: '#666',
  },
  priceValue: {
    fontSize: '18px',
    color: '#2E7D32',
    fontWeight: 'bold',
  },
  paymentNote: {
    fontSize: '11px',
    color: '#666',
    fontStyle: 'italic',
    marginTop: '5px',
  },
  planFooter: {
    marginTop: '15px',
    paddingTop: '10px',
    borderTop: '1px solid #eee',
    fontSize: '11px',
    color: '#999',
    display: 'flex',
    justifyContent: 'space-between',
  },
  editCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '30px',
    maxWidth: '900px',
    margin: '0 auto',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  editTitle: {
    fontSize: '20px',
    color: '#2E7D32',
    marginBottom: '20px',
  },
  editGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '30px',
    marginBottom: '30px',
  },
  editSection: {
    backgroundColor: '#F5F0E6',
    padding: '15px',
    borderRadius: '8px',
  },
  inputGroup: {
    marginBottom: '15px',
  },
  input: {
    width: '100%',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '14px',
  },
  pricingInput: {
    width: '100%',
    padding: '10px',
    border: '2px solid #FFD700',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 'bold',
    backgroundColor: '#FFF8E1',
  },
  hint: {
    fontSize: '11px',
    color: '#666',
    marginTop: '5px',
    display: 'block',
  },
  pricingHighlight: {
    backgroundColor: '#E8F5E9',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '15px',
  },
  pricingNote: {
    fontSize: '12px',
    color: '#1B5E20',
    margin: 0,
    lineHeight: '1.4',
  },
  infoBox: {
    backgroundColor: '#E3F2FD',
    padding: '10px',
    borderRadius: '5px',
    marginTop: '15px',
    fontSize: '12px',
  },
  infoList: {
    margin: '5px 0 0 20px',
    padding: 0,
  },
  editActions: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'flex-end',
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#2E7D32',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #666',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #ef4444',
    color: '#dc2626',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '20px',
  },
  successBox: {
    backgroundColor: '#d1fae5',
    border: '1px solid #10b981',
    color: '#065f46',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '20px',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '60px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #F5F0E6',
    borderTop: '4px solid #FFD700',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default PlanConfig;